import React, { useState, createContext, useContext } from 'react';
import { Project, Task, ProjectNote } from '../types/project';
import { projects as seedProjects } from '../utils/projectSeedData';
interface ProjectContextType {
  projects: Project[];
  getProjectsByCompanyId: (companyId: string) => Project[];
  getProjectsByContactId: (contactId: string) => Project[];
  addProject: (project: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  toggleProjectComplete: (id: string) => void;
  addTask: (projectId: string, task: Omit<Task, 'id'>) => void;
  updateTask: (projectId: string, taskId: string, updates: Partial<Task>) => void;
  deleteTask: (projectId: string, taskId: string) => void;
  toggleTaskComplete: (projectId: string, taskId: string) => void;
  addNote: (projectId: string, note: Omit<ProjectNote, 'id' | 'created_at'>) => void;
  updateNote: (projectId: string, noteId: string, updates: Partial<ProjectNote>) => void;
  deleteNote: (projectId: string, noteId: string) => void;
}
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);
export function ProjectProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [projects, setProjects] = useState<Project[]>(seedProjects);
  const getProjectsByCompanyId = (companyId: string) => {
    return projects.filter(project => project.company_id === companyId);
  };
  const getProjectsByContactId = (contactId: string) => {
    return projects.filter(project => project.contact_id === contactId);
  };
  const addProject = (projectData: Omit<Project, 'id' | 'created_at' | 'updated_at'>) => {
    const newProject: Project = {
      ...projectData,
      id: `proj-${Date.now()}`,
      notes: [],
      created_at: new Date(),
      updated_at: new Date()
    };
    setProjects([...projects, newProject]);
  };
  const updateProject = (id: string, updates: Partial<Project>) => {
    setProjects(projects.map(project => project.id === id ? {
      ...project,
      ...updates,
      updated_at: new Date()
    } : project));
  };
  const deleteProject = (id: string) => {
    setProjects(projects.filter(project => project.id !== id));
  };
  const toggleProjectComplete = (id: string) => {
    setProjects(projects.map(project => project.id === id ? {
      ...project,
      completed: !project.completed,
      updated_at: new Date()
    } : project));
  };
  const addTask = (projectId: string, taskData: Omit<Task, 'id'>) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        const newTask: Task = {
          ...taskData,
          id: `task-${Date.now()}`
        };
        return {
          ...project,
          tasks: [...project.tasks, newTask],
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const updateTask = (projectId: string, taskId: string, updates: Partial<Task>) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          tasks: project.tasks.map(task => task.id === taskId ? {
            ...task,
            ...updates
          } : task),
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const deleteTask = (projectId: string, taskId: string) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          tasks: project.tasks.filter(task => task.id !== taskId),
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const toggleTaskComplete = (projectId: string, taskId: string) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          tasks: project.tasks.map(task => task.id === taskId ? {
            ...task,
            completed: !task.completed
          } : task),
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const addNote = (projectId: string, noteData: Omit<ProjectNote, 'id' | 'created_at'>) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        const newNote: ProjectNote = {
          ...noteData,
          id: `note-${Date.now()}`,
          created_at: new Date()
        };
        return {
          ...project,
          notes: [...(project.notes || []), newNote],
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const updateNote = (projectId: string, noteId: string, updates: Partial<ProjectNote>) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          notes: (project.notes || []).map(note => note.id === noteId ? {
            ...note,
            ...updates
          } : note),
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const deleteNote = (projectId: string, noteId: string) => {
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        return {
          ...project,
          notes: (project.notes || []).filter(note => note.id !== noteId),
          updated_at: new Date()
        };
      }
      return project;
    }));
  };
  const value: ProjectContextType = {
    projects,
    getProjectsByCompanyId,
    getProjectsByContactId,
    addProject,
    updateProject,
    deleteProject,
    toggleProjectComplete,
    addTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,
    addNote,
    updateNote,
    deleteNote
  };
  return <ProjectContext.Provider value={value} data-id="element-413">{children}</ProjectContext.Provider>;
}
export function useProjects(): ProjectContextType {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
}